import { useId, type ComponentPropsWithoutRef } from "react";
import { AccentColor } from "@/lib/colors";

type TextFieldOwnProps = {
  label: string;
  error?: string;
  /** Fixed text shown before the input (e.g. `linkedin.com/in/`), so only
   *  the rest has to be typed. Not part of the value. */
  prefix?: string;
  hideErrorMessage?: boolean;
  color?: AccentColor;
};

export type TextFieldProps = TextFieldOwnProps &
  Omit<ComponentPropsWithoutRef<"input">, keyof TextFieldOwnProps>;

const base =
  "w-full border-b bg-transparent px-0 pt-1 pb-2 text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-b-2 focus:pb-[7px] transition-colors duration-200 font-body";

const colorClasses: Record<AccentColor, string> = {
  [AccentColor.Bubblegum]: "focus:border-bubblegum",
  [AccentColor.Lavender]: "focus:border-lavender",
  [AccentColor.Aegean]: "focus:border-aegean",
  [AccentColor.Success]: "focus:border-success",
  [AccentColor.Warning]: "focus:border-warning",
  [AccentColor.Error]: "focus:border-error"
};

/** The same colours for a prefixed field, whose row carries the underline.
 *  Written out in full: Tailwind only generates classes it finds literally. */
const colorWithinClasses: Record<AccentColor, string> = {
  [AccentColor.Bubblegum]: "focus-within:border-bubblegum",
  [AccentColor.Lavender]: "focus-within:border-lavender",
  [AccentColor.Aegean]: "focus-within:border-aegean",
  [AccentColor.Success]: "focus-within:border-success",
  [AccentColor.Warning]: "focus-within:border-warning",
  [AccentColor.Error]: "focus-within:border-error"
};

export function TextField({
  label,
  error,
  hideErrorMessage = false,
  color = AccentColor.Bubblegum,
  className,
  prefix,
  ...props
}: TextFieldProps) {
  const id = useId();
  // The same size a plain field gets: `base` carries `text-base` and the
  // caller's className is appended, so the two resolve in the same order.
  const prefixedText = className ? `text-base ${className}` : "text-base";
  const inputClass = error
    ? `${base} border-error`
    : `${base} border-border ${colorClasses[color]}`;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label htmlFor={id} className="text-sm text-text-body font-body">
          {label}
        </label>
      )}
      {prefix ? (
        // The underline moves to the row, so the prefix sits on it too and
        // focus styles the whole field.
        <div
          className={`flex items-baseline border-b transition-colors duration-200 focus-within:border-b-2 focus-within:mb-[-1px] ${
            error
              ? "border-error"
              : `border-border ${colorWithinClasses[color]}`
          }`}
        >
          <span
            aria-hidden="true"
            className={`shrink-0 pt-1 pb-2 text-text-muted font-body select-none ${prefixedText}`}
          >
            {prefix}
          </span>
          <input
            id={id}
            className={`w-full min-w-0 bg-transparent px-0 pt-1 pb-2 text-text-primary placeholder:text-text-muted focus:outline-none font-body ${prefixedText}`}
            {...props}
          />
        </div>
      ) : (
        <input
          id={id}
          className={className ? `${inputClass} ${className}` : inputClass}
          {...props}
        />
      )}
      {error && !hideErrorMessage && (
        <p className="text-xs text-error font-body">{error}</p>
      )}
    </div>
  );
}
